<script setup>
import { ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import { clampAllocationDateTimeFields } from 'dashboard/components-next/planning/Scheduler/allocationDateTimeFields';
import EventSidebarSection from './EventSidebarSection.vue';

const props = defineProps({
  locations: { type: Array, default: () => [] },
  date: { type: String, default: '' },
  startTime: { type: String, default: '' },
  endTime: { type: String, default: '' },
  pax: { type: [Number, String], default: null },
  editingDate: { type: Boolean, default: false },
  editingTime: { type: Boolean, default: false },
  tempStartDate: { type: String, default: '' },
  tempStartTime: { type: String, default: '' },
  tempEndDate: { type: String, default: '' },
  tempEndTime: { type: String, default: '' },
  canEditDateTime: { type: Boolean, default: false },
  editingLocation: { type: Boolean, default: false },
  tempResourceId: { type: [String, Number], default: null },
  resources: { type: Array, default: () => [] },
  canEditLocation: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  collapsed: { type: Boolean, default: false },
  readOnly: { type: Boolean, default: false },
});

const emit = defineEmits([
  'startEditDate',
  'startEditTime',
  'saveDateTime',
  'cancelEditDate',
  'cancelEditTime',
  'update:tempStartDate',
  'update:tempStartTime',
  'update:tempEndDate',
  'update:tempEndTime',
  'startEditLocation',
  'saveLocation',
  'cancelEditLocation',
  'update:tempResourceId',
  'editAllocations',
  'toggleCollapse',
]);

const { t } = useI18n();

const resourceOptionLabel = resource => {
  if (resource.parent_resource_id) return `— ${resource.name}`;
  return resource.name;
};

const startDateInput = ref(null);
const startTimeInput = ref(null);

const emitClampedFields = fields => {
  if (fields.startDate !== props.tempStartDate) {
    emit('update:tempStartDate', fields.startDate);
  }
  if (fields.startTime !== props.tempStartTime) {
    emit('update:tempStartTime', fields.startTime);
  }
  if (fields.endDate !== props.tempEndDate) {
    emit('update:tempEndDate', fields.endDate);
  }
  if (fields.endTime !== props.tempEndTime) {
    emit('update:tempEndTime', fields.endTime);
  }
};

const onStartDateInput = value => {
  emitClampedFields(
    clampAllocationDateTimeFields(
      {
        startDate: value,
        startTime: props.tempStartTime,
        endDate: props.tempEndDate,
        endTime: props.tempEndTime,
      },
      'start'
    )
  );
};

const onEndDateInput = value => {
  emitClampedFields(
    clampAllocationDateTimeFields(
      {
        startDate: props.tempStartDate,
        startTime: props.tempStartTime,
        endDate: value,
        endTime: props.tempEndTime,
      },
      'end'
    )
  );
};

const onStartTimeInput = value => {
  emitClampedFields(
    clampAllocationDateTimeFields(
      {
        startDate: props.tempStartDate,
        startTime: value,
        endDate: props.tempEndDate,
        endTime: props.tempEndTime,
      },
      'start'
    )
  );
};

const onEndTimeInput = value => {
  emitClampedFields(
    clampAllocationDateTimeFields(
      {
        startDate: props.tempStartDate,
        startTime: props.tempStartTime,
        endDate: props.tempEndDate,
        endTime: value,
      },
      'end'
    )
  );
};

watch(
  () => props.editingDate,
  async editing => {
    if (!editing) return;
    await nextTick();
    startDateInput.value?.focus();
  }
);

watch(
  () => props.editingTime,
  async editing => {
    if (!editing) return;
    await nextTick();
    startTimeInput.value?.focus();
  }
);
</script>

<template>
  <EventSidebarSection
    :title="t('PLANNING.EVENTS.OVERVIEW')"
    icon="i-lucide-info"
  >
    <ul
      class="m-0 flex list-none flex-col gap-2 p-0 text-base font-normal text-n-slate-12"
    >
      <li class="flex items-start gap-3">
        <Icon
          icon="i-lucide-orbit"
          class="mt-0.5 size-4 shrink-0 text-n-slate-9 dark:text-n-slate-2"
        />
        <div
          v-if="editingLocation"
          class="flex min-w-0 items-center gap-2 print:hidden"
        >
          <select
            :value="tempResourceId"
            class="reset-base min-w-0 flex-1 cursor-pointer border-b-2 border-woot-500 bg-transparent py-1 text-base font-normal text-slate-900 focus:outline-none dark:text-white"
            @change="emit('update:tempResourceId', $event.target.value)"
          >
            <option :value="null">
              {{ t('PLANNING.SHOW.UNASSIGNED_SPACE') }}
            </option>
            <option v-for="r in resources" :key="r.id" :value="r.id">
              {{ resourceOptionLabel(r) }}
            </option>
          </select>
          <button
            class="p-1 text-woot-600 hover:text-woot-700"
            :disabled="saving || !tempResourceId"
            @click="emit('saveLocation')"
          >
            <Icon icon="i-lucide-check" class="size-4" />
          </button>
          <button
            class="p-1 text-slate-400 hover:text-slate-600"
            @click="emit('cancelEditLocation')"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </div>
        <div
          v-else
          class="min-w-0"
          :class="{
            'cursor-pointer transition-colors hover:text-woot-600 print:cursor-default':
              canEditLocation,
          }"
          @click="canEditLocation && emit('startEditLocation')"
        >
          <span v-if="locations.length" class="text-sm line-clamp-3 leading-5">
            {{ locations.join(', ') }}
          </span>
          <span v-else class="text-n-slate-9">{{
            t('PLANNING.SHOW.SET_LOCATION')
          }}</span>
        </div>
      </li>

      <li class="flex items-start gap-3">
        <Icon
          icon="i-lucide-calendar"
          class="mt-0.5 size-4 shrink-0 text-n-slate-9 dark:text-n-slate-2"
        />
        <div
          v-if="editingDate"
          class="flex flex-wrap items-center gap-2 print:hidden"
        >
          <input
            ref="startDateInput"
            :value="tempStartDate"
            type="date"
            class="reset-base no-margin border-b-2 border-woot-500 bg-transparent px-1 text-base font-normal text-slate-900 focus:outline-none dark:text-white"
            @input="onStartDateInput($event.target.value)"
            @keyup.enter="emit('saveDateTime')"
            @keyup.escape="emit('cancelEditDate')"
          />
          <span class="text-slate-400">–</span>
          <input
            :value="tempEndDate"
            type="date"
            class="reset-base no-margin border-b-2 border-woot-500 bg-transparent px-1 text-base font-normal text-slate-900 focus:outline-none dark:text-white"
            @input="onEndDateInput($event.target.value)"
            @keyup.enter="emit('saveDateTime')"
            @keyup.escape="emit('cancelEditDate')"
          />
          <button
            class="p-1 text-woot-600 hover:text-woot-700"
            :disabled="saving"
            @click="emit('saveDateTime')"
          >
            <Icon icon="i-lucide-check" class="size-4" />
          </button>
          <button
            class="p-1 text-slate-400 hover:text-slate-600"
            @click="emit('cancelEditDate')"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </div>
        <span
          v-else
          class="min-w-0 truncate text-sm leading-5"
          :class="{
            'cursor-pointer transition-colors hover:text-woot-600 print:cursor-default':
              canEditDateTime,
          }"
          @click="canEditDateTime && emit('startEditDate')"
        >
          {{ date }}
        </span>
      </li>

      <li class="flex items-start gap-3">
        <Icon
          icon="i-lucide-clock"
          class="mt-0.5 size-4 shrink-0 text-n-slate-9 dark:text-n-slate-2"
        />
        <div
          v-if="editingTime"
          class="flex flex-wrap items-center gap-2 print:hidden"
        >
          <input
            ref="startTimeInput"
            :value="tempStartTime"
            type="time"
            class="reset-base no-margin border-b-2 border-woot-500 bg-transparent px-1 text-base font-normal text-slate-900 focus:outline-none dark:text-white"
            @input="emit('update:tempStartTime', $event.target.value)"
            @blur="onStartTimeInput($event.target.value)"
            @keyup.enter="
              onStartTimeInput($event.target.value);
              emit('saveDateTime');
            "
            @keyup.escape="emit('cancelEditTime')"
          />
          <span class="text-slate-400">–</span>
          <input
            :value="tempEndTime"
            type="time"
            class="reset-base no-margin border-b-2 border-woot-500 bg-transparent px-1 text-base font-normal text-slate-900 focus:outline-none dark:text-white"
            @input="emit('update:tempEndTime', $event.target.value)"
            @blur="onEndTimeInput($event.target.value)"
            @keyup.enter="
              onEndTimeInput($event.target.value);
              emit('saveDateTime');
            "
            @keyup.escape="emit('cancelEditTime')"
          />
          <button
            class="p-1 text-woot-600 hover:text-woot-700"
            :disabled="saving"
            @click="emit('saveDateTime')"
          >
            <Icon icon="i-lucide-check" class="size-4" />
          </button>
          <button
            class="p-1 text-slate-400 hover:text-slate-600"
            @click="emit('cancelEditTime')"
          >
            <Icon icon="i-lucide-x" class="size-4" />
          </button>
        </div>
        <span
          v-else
          class="min-w-0 truncate text-sm leading-5"
          :class="{
            'cursor-pointer transition-colors hover:text-woot-600 print:cursor-default':
              canEditDateTime,
          }"
          @click="canEditDateTime && emit('startEditTime')"
        >
          {{ startTime }} - {{ endTime }}
        </span>
      </li>

      <li class="flex items-start gap-3">
        <Icon
          icon="i-lucide-users"
          class="mt-0.5 size-4 shrink-0 text-n-slate-9 dark:text-n-slate-2"
        />
        <span class="min-w-0 truncate text-sm leading-5">
          {{
            pax
              ? `${pax} ${t('PLANNING.SHOW.GUESTS')}`
              : t('PLANNING.SHOW.SET_GUESTS')
          }}
        </span>
      </li>
    </ul>
  </EventSidebarSection>
</template>
